import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import RightSideSummary from "./RightSideSummary";
import {
  HomeIcon,
  BuildingOfficeIcon,
  UserIcon,
  PhoneIcon,
  BuildingOffice2Icon,
} from "@heroicons/react/24/outline";

// Validation utility functions
const validationRules = {
  streetAddress: {
    required: true,
    minLength: 5,
    maxLength: 200,
    message: "Please enter a valid street address"
  },
  businessName: {
    required: false,
    minLength: 2,
    maxLength: 100,
    message: "Business name must be between 2 and 100 characters"
  },
  contactName: {
    required: false,
    minLength: 2,
    maxLength: 100,
    pattern: /^[a-zA-Z\s.'-]+$/,
    message: "Please enter a valid full name"
  },
  contactPhone: {
    required: false,
    pattern: /^\(\d{3}\) \d{3}-\d{4}$/,
    message: "Please enter a valid 10-digit phone number"
  },
  aptSuite: {
    required: false,
    maxLength: 50,
    message: "Apt/Suite must be less than 50 characters"
  },
  specialInstructions: {
    required: false,
    maxLength: 500,
    message: "Special instructions must be less than 500 characters"
  }
};

const validateField = (name, value, rule) => {
  if (rule.required && !value.trim()) {
    return `${name} is required`;
  }
  
  if (value.trim() && rule.minLength && value.trim().length < rule.minLength) {
    return `${name} must be at least ${rule.minLength} characters`;
  }
  
  if (value.trim() && rule.maxLength && value.trim().length > rule.maxLength) {
    return `${name} must be less than ${rule.maxLength} characters`;
  }
  
  if (value.trim() && rule.pattern && !rule.pattern.test(value.trim())) {
    return rule.message;
  }
  
  return "";
};

const Delivery = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [quoteData, setQuoteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const API_BASE_URL = 'https://www.gfapi.net' || ""; 
  
  const [contactOtherDelivery, setContactOtherDelivery] = useState(false);
  const [specialInstructionVisible, setSpecialInstructionVisible] = useState(false);
  const [isResidential, setIsResidential] = useState(true);

  const [formData, setFormData] = useState({
    streetAddress: "",
    aptSuite: "",
    businessName: "",
    contactName: "",
    contactPhone: "",
    specialInstructions: "",
  });

  // Validate entire form
  const validateForm = () => {
    const errors = {};
    
    // Validate street address (always required)
    const streetAddressError = validateField(
      "Street address",
      formData.streetAddress,
      validationRules.streetAddress
    );
    if (streetAddressError) errors.streetAddress = streetAddressError;
    
    // Validate business name if business address
    if (!isResidential) {
      const businessNameRule = {
        ...validationRules.businessName,
        required: true
      };
      const businessNameError = validateField(
        "Business name",
        formData.businessName,
        businessNameRule
      );
      if (businessNameError) errors.businessName = businessNameError;
    }
    
    // Validate contact name and phone if "Contact someone else" is selected
    if (contactOtherDelivery) {
      const contactNameRule = {
        ...validationRules.contactName,
        required: true
      };
      const contactNameError = validateField(
        "Delivery contact's full name",
        formData.contactName,
        contactNameRule
      );
      if (contactNameError) errors.contactName = contactNameError;
      
      const contactPhoneRule = {
        ...validationRules.contactPhone,
        required: true
      };
      const contactPhoneError = validateField(
        "Delivery contact's phone number",
        formData.contactPhone,
        contactPhoneRule
      );
      if (contactPhoneError) errors.contactPhone = contactPhoneError;
    }
    
    // Validate optional fields only if they have content
    if (formData.aptSuite.trim()) {
      const aptSuiteError = validateField(
        "Apt/Suite",
        formData.aptSuite,
        validationRules.aptSuite
      );
      if (aptSuiteError) errors.aptSuite = aptSuiteError;
    }
    
    if (formData.specialInstructions.trim()) {
      const specialInstructionsError = validateField(
        "Special instructions",
        formData.specialInstructions,
        validationRules.specialInstructions
      );
      if (specialInstructionsError) errors.specialInstructions = specialInstructionsError;
    }
    
    return errors;
  };

  // Field blur handler
  const handleBlur = (field) => {
    setTouchedFields(prev => ({ ...prev, [field]: true }));
    
    // Validate specific field
    if (field === 'streetAddress') {
      const error = validateField("Street address", formData.streetAddress, validationRules.streetAddress);
      setValidationErrors(prev => ({ ...prev, streetAddress: error }));
    } else if (field === 'businessName') {
      const rule = !isResidential 
        ? { ...validationRules.businessName, required: true }
        : validationRules.businessName;
      const error = validateField("Business name", formData.businessName, rule);
      setValidationErrors(prev => ({ ...prev, businessName: error }));
    } else if (field === 'contactName') {
      const rule = contactOtherDelivery 
        ? { ...validationRules.contactName, required: true }
        : validationRules.contactName;
      const error = validateField("Delivery contact's full name", formData.contactName, rule);
      setValidationErrors(prev => ({ ...prev, contactName: error }));
    } else if (field === 'contactPhone') {
      const rule = contactOtherDelivery 
        ? { ...validationRules.contactPhone, required: true }
        : validationRules.contactPhone;
      const error = validateField("Delivery contact's phone number", formData.contactPhone, rule);
      setValidationErrors(prev => ({ ...prev, contactPhone: error }));
    } else if (field === 'aptSuite') {
      const error = validateField("Apt/Suite", formData.aptSuite, validationRules.aptSuite);
      setValidationErrors(prev => ({ ...prev, aptSuite: error }));
    } else if (field === 'specialInstructions') {
      const error = validateField("Special instructions", formData.specialInstructions, validationRules.specialInstructions);
      setValidationErrors(prev => ({ ...prev, specialInstructions: error }));
    }
  };

  // Clear validation errors when switching address type
useEffect(() => {
  if (isResidential && validationErrors.businessName) {
    setValidationErrors(prev => ({ ...prev, businessName: "" }));
  }
}, [isResidential, validationErrors.businessName]);


  // Clear validation errors when switching contact type
  useEffect(() => {
  if (!contactOtherDelivery) {
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.contactName;
      delete newErrors.contactPhone;
      return newErrors;
    });
  }
}, [contactOtherDelivery]);

  /* ================= GET QUOTE ================= */
  useEffect(() => {
    if (!id) return;

    const fetchQuote = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/quotes/${id}`
        );

        if (!res.ok) throw new Error("Failed to load quote");

        const json = await res.json();
        if (!json?.success) throw new Error("Quote not found");

        setQuoteData(json.data);
        
        // Pre-fill form data if available
        if (json.data?.deliveryDetails) {
          const details = json.data.deliveryDetails;
          const newFormData = {
            streetAddress: details.streetAddress || "",
            aptSuite: details.aptSuite || "",
            businessName: details.businessName || "",
            contactName: details.deliveryContact?.name || "",
            contactPhone: details.deliveryContact?.phone 
              ? formatPhoneNumber(details.deliveryContact.phone)
              : "",
            specialInstructions: details.specialInstructions || "",
          };
          setFormData(newFormData);
          
          // Set state based on saved data
          if (details.isResidential !== undefined) {
            setIsResidential(details.isResidential);
          }
          if (details.contactOtherDelivery !== undefined) {
            setContactOtherDelivery(details.contactOtherDelivery);
          }
          if (details.specialInstructions) {
            setSpecialInstructionVisible(true);
          }
        }
      } catch (err) {
        console.error("Delivery GET quote error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchQuote();
  }, [id]);

  // Helper to format phone number for display
  const formatPhoneNumber = (phone) => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length === 10) {
      return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6, 10)}`;
    }
    return phone;
  };

  // Handle phone number input with formatting
  const handlePhoneChange = (value) => {
    const digits = value.replace(/\D/g, "");
    
    let formatted = "";
    if (digits.length > 0) {
      formatted = "(" + digits.substring(0, 3);
    }
    if (digits.length >= 4) {
      formatted += ") " + digits.substring(3, 6);
    }
    if (digits.length >= 7) {
      formatted += "-" + digits.substring(6, 10);
    }
    
    setFormData(prev => ({ ...prev, contactPhone: formatted }));
    
    // Clear validation error
    if (validationErrors.contactPhone) {
      setValidationErrors(prev => ({ ...prev, contactPhone: "" }));
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const normalizePhone = (val = "") =>
    val.replace(/\D/g, "").slice(0, 10);

  // Handle address type change
  const handleAddressTypeChange = (residential) => {
    setIsResidential(residential);
    // Clear business name error if switching to residential
    if (residential && validationErrors.businessName) {
      setValidationErrors(prev => ({ ...prev, businessName: "" }));
    }
  };

  // Handle contact type change
  const handleContactTypeChange = (other) => {
    setContactOtherDelivery(other);
    // Clear contact errors if switching to "Contact me"
    if (!other) {
      const newErrors = { ...validationErrors };
      delete newErrors.contactName;
      delete newErrors.contactPhone;
      setValidationErrors(newErrors);
    }
  };

  // Handle special instructions toggle
  const handleSpecialInstructionsToggle = () => {
    if (!specialInstructionVisible) {
      setSpecialInstructionVisible(true);
    } else {
      setSpecialInstructionVisible(false);
      setFormData(prev => ({ ...prev, specialInstructions: "" }));
      if (validationErrors.specialInstructions) {
        setValidationErrors(prev => ({ ...prev, specialInstructions: "" }));
      }
    }
  };

  // Helper to show error message
  const renderError = (field) => {
    if (validationErrors[field] && (touchedFields[field] || updating)) {
      return (
        <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {validationErrors[field]}
        </p>
      );
    }
    return null;
  };

  /* ================= CONTINUE (PATCH) ================= */
  const handleContinue = async () => {
    if (!quoteData?._id || updating) return;

    // Validate form
    const errors = validateForm();
    setValidationErrors(errors);
    
    // Mark all relevant fields as touched
    const touched = {
      streetAddress: true,
    };
    
    if (!isResidential) {
      touched.businessName = true;
    }
    
    if (contactOtherDelivery) {
      touched.contactName = true;
      touched.contactPhone = true;
    }
    
    // Mark optional fields as touched if they have content
    if (formData.aptSuite.trim()) touched.aptSuite = true;
    if (formData.specialInstructions.trim()) touched.specialInstructions = true;
    
    setTouchedFields(touched);
    
    // Check if there are any errors
    if (Object.keys(errors).some(key => errors[key])) {
      // Scroll to first error
      const firstErrorField = Object.keys(errors).find(key => errors[key]);
      const element = document.querySelector(`[data-field="${firstErrorField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
      return;
    }

    setUpdating(true);

    const payload = {
      deliveryDetails: {
        streetAddress: formData.streetAddress.trim(),
        aptSuite: formData.aptSuite.trim(),
        isResidential,
        businessName: !isResidential ? formData.businessName.trim() : "",
        contactOtherDelivery,
        deliveryContact: contactOtherDelivery
          ? {
              name: formData.contactName.trim(),
              phone: normalizePhone(formData.contactPhone),
            }
          : null,
        specialInstructions: formData.specialInstructions.trim(),
      },
    };

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/quotes/${quoteData._id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        const txt = await res.text();
        console.error("Update failed:", txt);
        alert("Unable to continue. Please try again.");
        return;
      }

      const json = await res.json();
      if (!json?.success) {
        alert("Update failed. Please try again.");
        return;
      }

      // ✅ FINAL STEP
      navigate(`/bookshipment/${quoteData._id}`);
    } catch (err) {
      console.error("Delivery update error:", err);
      alert("Network error. Please try again.");
    } finally {
      setUpdating(false);
    }
  };

  /* ================= LOADING ================= */
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F7FB] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00A1EF]" />
      </div>
    );
  }
 const formatDate = (date) => {
  if (!date) return null;

  return new Date(date).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// main logic
const pickupDate = quoteData?.pickupDate;

// agar user ki date full ho / option "date" ho to usi ko pick karo
// warna first available date use karo
const firstAvailableDate =
  pickupDate?.option === 'date'
    ? formatDate(pickupDate.date)
    : formatDate(quoteData?.firstAvailableDate);
  /* ================= RIGHT SIDE DATA ================= */
  const deliveryBookingSummary = quoteData
    ? {
        distance: quoteData.distanceMiles
          ? `${quoteData.distanceMiles.toFixed(1)} mi`
          : "N/A",
          firstAvailableDate,
        shipFrom: `${quoteData.pickup?.city || ""}, ${quoteData.pickup?.state || ""} ${quoteData.pickup?.zip || ""}`,
    shipTo: `${quoteData.dropoff?.city || ""}, ${quoteData.dropoff?.state || ""} ${quoteData.dropoff?.zip || ""}`,
        transportType:
          quoteData.carrier === "enclosed" ? "Enclosed" : "Open",
        serviceType: quoteData.bookingMeta?.serviceType || "Door to Door",
        insurance: quoteData.bookingMeta?.insurance || "Included",
        transitTime: quoteData.bookingMeta?.transitTime || "N/A",
      }
    : {};

  const v = quoteData?.vehicles?.[0];

  const deliveryVehicleDetails = quoteData
    ? {
        vehicleMake: `${v?.year || ""} ${v?.make || ""}`.trim(),
        vehicleModel: v?.model || "",
        vehicleCondition: v?.isOperable ? "Running" : "Non-Running",
      }
    : {};

  return (
    <div className="min-h-screen bg-[#F5F7FB]">
      <div className="p-4 md:p-8 max-w-6xl mx-auto flex flex-col lg:flex-row gap-8">
        {/* LEFT SIDE - FORM */}
        <div className="flex-1">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h1 className="text-2xl font-bold text-[#103558] mb-2">Vehicle Delivery Details</h1>
            <p className="text-gray-600 mb-6">
              Tell us where to deliver your vehicle
            </p>

            {/* ADDRESS SECTION */}
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-[#103558] mb-3">Delivery Location</h2>
              
              {/* Street Address */}
              <div className="mb-3" data-field="streetAddress">
                <label className="block text-sm font-medium text-[#103558] mb-1">
                  Street address *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.streetAddress}
                    onChange={(e) => handleInputChange("streetAddress", e.target.value)}
                    onBlur={() => handleBlur("streetAddress")}
                    placeholder={isResidential ? "Enter street address" : "Enter business street address"}
                    className={`w-full px-3 py-2.5 pl-10 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:border-transparent ${
                      validationErrors.streetAddress && touchedFields.streetAddress
                        ? "border-red-300 focus:ring-red-500"
                        : "border-gray-300 focus:ring-[#00A1EF]"
                    }`}
                    required
                  />
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                    {isResidential ? (
                      <HomeIcon className="w-4 h-4" />
                    ) : (
                      <BuildingOfficeIcon className="w-4 h-4" />
                    )}
                  </div>
                </div>
                {renderError("streetAddress")}
              </div>

              {/* APT/SUITE OPTIONAL */}
              <div className="mb-4" data-field="aptSuite">
                <label className="block text-sm font-medium text-[#103558] mb-1">
                  Apt, suite, etc. <span className="text-gray-500 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={formData.aptSuite}
                  onChange={(e) => handleInputChange("aptSuite", e.target.value)}
                  onBlur={() => handleBlur("aptSuite")}
                  placeholder="Apt, suite, unit, building, etc."
                  className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:border-transparent ${
                    validationErrors.aptSuite && touchedFields.aptSuite
                      ? "border-red-300 focus:ring-red-500"
                      : "border-gray-300 focus:ring-[#00A1EF]"
                  }`}
                />
                {renderError("aptSuite")}
              </div>

           
            </div>

            {/* ADDRESS TYPE */}
            <div className="mb-6">
              <p className="text-sm font-medium text-[#103558] mb-2">Address type</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={() => handleAddressTypeChange(true)}
                  className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${
                    isResidential
                    ? "border-[#00A1EF] bg-blue-50"
                    : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                    isResidential
                    ? "border-[#00A1EF] bg-[#00A1EF]"
                    : "border-gray-300"
                  }`}>
                    {isResidential && (
                      <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                    )}
                  </div>
                  <div className="text-left">
                    <span className="text-sm text-gray-700 font-medium">Residential address</span>
                    <p className="text-xs text-gray-500">House, apartment, condo, etc.</p>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => handleAddressTypeChange(false)}
                  className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${
                    !isResidential
                    ? "border-[#00A1EF] bg-blue-50"
                    : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                    !isResidential
                    ? "border-[#00A1EF] bg-[#00A1EF]"
                    : "border-gray-300"
                  }`}>
                    {!isResidential && (
                      <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                    )}
                  </div>
                  <div className="text-left">
                    <span className="text-sm text-gray-700 font-medium">Business address</span>
                    <p className="text-xs text-gray-500">Office, dealership, lot, etc.</p>
                  </div>
                </button>
              </div>

              {/* Business Name (Only for Business Address) */}
              {!isResidential && (
                <div className="mt-4" data-field="businessName">
                  <label className="block text-sm font-medium text-[#103558] mb-1">
                    Business name *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.businessName}
                      onChange={(e) => handleInputChange("businessName", e.target.value)}
                      onBlur={() => handleBlur("businessName")}
                      placeholder="Enter business name"
                      className={`w-full px-3 py-2.5 pl-10 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:border-transparent ${
                        validationErrors.businessName && touchedFields.businessName
                          ? "border-red-300 focus:ring-red-500"
                          : "border-gray-300 focus:ring-[#00A1EF]"
                      }`}
                      required
                    />
                    <BuildingOffice2Icon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  </div>
                  {renderError("businessName")}
                </div>
              )}
            </div>

            {/* CONTACT PERSON */}
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-[#103558] mb-3">Delivery Contact</h2>
              <p className="text-sm font-medium text-[#103558] mb-2">
                Who should we contact about delivery?
              </p>
              
              <div className="flex flex-col gap-2 mb-4">
                <button
                  type="button"
                  onClick={() => handleContactTypeChange(false)}
                  className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${
                    !contactOtherDelivery
                    ? "border-[#00A1EF] bg-blue-50"
                    : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                    !contactOtherDelivery
                    ? "border-[#00A1EF] bg-[#00A1EF]"
                    : "border-gray-300"
                  }`}>
                    {!contactOtherDelivery && (
                      <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                    )}
                  </div>
                  <div className="text-left">
                    <span className="text-sm text-gray-700 font-medium">Contact me</span>
                    <p className="text-xs text-gray-500">Use my primary contact information</p>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => handleContactTypeChange(true)}
                  className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${
                    contactOtherDelivery
                    ? "border-[#00A1EF] bg-blue-50"
                    : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                    contactOtherDelivery
                    ? "border-[#00A1EF] bg-[#00A1EF]"
                    : "border-gray-300"
                  }`}>
                    {contactOtherDelivery && (
                      <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                    )}
                  </div>
                  <div className="text-left">
                    <span className="text-sm text-gray-700 font-medium">Contact someone else</span>
                    <p className="text-xs text-gray-500">Different person at delivery location</p>
                  </div>
                </button>
              </div>

              {/* CONDITIONAL CONTACT FIELDS */}
              {contactOtherDelivery && (
                <div className="space-y-3 mb-4">
                  <div data-field="contactName">
                    <label className="block text-sm font-medium text-[#103558] mb-1">
                      Delivery contact's full name *
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={formData.contactName}
                        onChange={(e) => handleInputChange("contactName", e.target.value)}
                        onBlur={() => handleBlur("contactName")}
                        placeholder="Enter full name"
                        className={`w-full px-3 py-2.5 pl-10 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:border-transparent ${
                          validationErrors.contactName && touchedFields.contactName
                            ? "border-red-300 focus:ring-red-500"
                            : "border-gray-300 focus:ring-[#00A1EF]"
                        }`}
                        required
                      />
                      <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    </div>
                    {renderError("contactName")}
                  </div>

                  <div data-field="contactPhone">
                    <label className="block text-sm font-medium text-[#103558] mb-1">
                      Delivery contact's phone number *
                    </label>
                    <div className="relative">
                      <input
                        type="tel"
                        value={formData.contactPhone}
                        onChange={(e) => handlePhoneChange(e.target.value)}
                        onBlur={() => handleBlur("contactPhone")}
                        placeholder="(212) 345-6789"
                        className={`w-full px-3 py-2.5 pl-10 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:border-transparent ${
                          validationErrors.contactPhone && touchedFields.contactPhone
                            ? "border-red-300 focus:ring-red-500"
                            : "border-gray-300 focus:ring-[#00A1EF]"
                        }`}
                        maxLength="14"
                        required
                      />
                      <PhoneIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    </div>
                    {renderError("contactPhone")}
                  </div>
                </div>
              )}
            </div>

            {/* SPECIAL INSTRUCTIONS */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#103558] mb-2">
                Special instructions (optional)
              </label>
              
              {!specialInstructionVisible ? (
                <button
                  type="button"
                  onClick={handleSpecialInstructionsToggle}
                  className="flex items-center gap-1 text-[#00A1EF] hover:text-[#0088CC] font-medium p-2.5 rounded-lg border border-dashed border-gray-300 hover:border-[#00A1EF] transition-colors w-full justify-center text-sm"
                >
                  <span className="text-base">+</span>
                  <span>Add special instructions</span>
                </button>
              ) : (
                <div className="space-y-2" data-field="specialInstructions">
                  <textarea
                    value={formData.specialInstructions}
                    onChange={(e) => handleInputChange("specialInstructions", e.target.value)}
                    onBlur={() => handleBlur("specialInstructions")}
                    rows="2"
                    placeholder="Any special delivery instructions, gate codes, parking restrictions, preferred delivery times, etc."
                    className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:border-transparent resize-none ${
                      validationErrors.specialInstructions && touchedFields.specialInstructions
                        ? "border-red-300 focus:ring-red-500"
                        : "border-gray-300 focus:ring-[#00A1EF]"
                    }`}
                  />
                  {renderError("specialInstructions")}
                  <button
                    type="button"
                    onClick={handleSpecialInstructionsToggle}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    Remove instructions
                  </button>
                </div>
              )}
            </div>

            {/* NAVIGATION */}
            <div className="pt-4 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={() => navigate(`/pickup/${id}`)}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  ← Back to Pickup
                </button>
                
                <button
                  type="button"
                  onClick={handleContinue}
                  disabled={updating}
                  className={`flex-1 px-4 py-2.5 rounded-lg shadow transition-all duration-200 text-sm flex items-center justify-center gap-1
                    ${
                      updating
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-[#00A1EF] to-[#0088CC] hover:from-[#0088CC] hover:to-[#0077B3] text-white font-semibold hover:shadow-md"
                    }
                  `}
                >
                  {updating ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      Book shipment
                      <span className="text-lg">→</span>
                    </>
                  )}
                </button>
              </div>
              
              {/* Form validation summary */}
              {Object.keys(validationErrors).some(key => validationErrors[key]) && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm font-medium flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    Please fix the errors above before continuing
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT SIDE - USING REUSABLE COMPONENT */}
        <RightSideSummary
          title="Details"
          bookingSummary={deliveryBookingSummary}
          vehicleDetails={deliveryVehicleDetails}
          editable={false}
          showPromoCode={false}
          priceDetails={{
            dueNow: "$0",
            totalDue:
              quoteData?.pricing?.selectedPriceType === "discount"
                ? `$${quoteData?.pricing?.discountPrice ?? "0"}`
                : `$${quoteData?.pricing?.regularPrice ?? "0"}`,
            priceLabel: "Total due",
          }}
        />
      </div>
    </div>
  );
};

export default Delivery;